import React, { useContext, useState, useEffect } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { InputFormGroup, CheckBoxFormGroup } from '../Common/Input/FormFields';
import { Grid, Cell, Button } from 'react-foundation';
import { AdditionalContact } from '../../models/UserPreferences';
import SimpleModal from '../Common/SimpleModal';
import { SimpleModalContent } from '../Common/SimpleModalContent';
import UserContext from '../../UserContext';
import { SaveEvent, CheckboxEvent, ClickEvent } from '../Common/AnalyticsEvents';

const MAX_CONTACTS = 5;

const emptyContact: AdditionalContact = { fullName: '', email: '', phoneNumber: '', isReceivingNewJobOpportunities: true, isReceivingJobAwarded: true, isReceivingCommunicationResponse: true };

const AdditionalNotificationContacts: React.FC = () => {
  const user = useContext(UserContext);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [pendingRemoveIdx, setPendingRemoveIdx] = useState<number | null>(null); 
  const { values, setFieldValue, submitCount, submitForm } = useFormikContext<any>();
  const contacts: AdditionalContact[] = values.additionalContacts || [];
  const showEmail = values.communicationMethods?.includes('Email');
  const showPhone = values.communicationMethods?.includes('Text Message');
  const isDisabled = contacts.length >= MAX_CONTACTS

  useEffect(() => {
    if (!showEmail && contacts.some(c => c.email)) {
        const cleared = contacts.map((c: any) => ({ ...c, email: '' }));
        setFieldValue('additionalContacts', cleared);
    }
    if (!showPhone && contacts.some(c => c.phoneNumber)) {
        const cleared = contacts.map((c: any) => ({ ...c, phoneNumber: '' }));
        setFieldValue('additionalContacts', cleared);
    }
}, [showEmail, showPhone, contacts, setFieldValue]);

  return (
    <Cell className="card additional-contacts-card">
      <Cell>
        <h6 className="margin-none color-black preferences-section-title"><span className="far fa-user-group margin-none"></span> <b>Additional Notification Contacts</b></h6>
        <p className="font-small color-black">
            Add team members who should receive bid and case notifications
        </p>
      </Cell>
      <Grid className="bold additional-contacts-header-row">
        <Cell className="flex-two">Full Name</Cell>
        {showEmail && <Cell className="flex-two">Email</Cell>}
        {showPhone && <Cell className="flex-two">Phone Number</Cell>}
        <Cell className="flex-one">Actions</Cell>
      </Grid>
      <FieldArray name="additionalContacts">
        {({ push, remove }) => (
          <>
            {contacts.map((contact, idx) => (
            <React.Fragment key={idx}>
              <Grid className="additionalContactRow" key={idx}>
                <Cell className="additionalContactRowInput flex-two">
                  <InputFormGroup
                    className="margin-none"
                    name={`additionalContacts[${idx}].fullName`}
                    placeholder="Full Name"
                    noLabel
                    showErrorsOnlyOnSubmit
                    submitCount={submitCount}
                  />
                </Cell>
                {showEmail && (
                  <Cell className="additionalContactRowInput flex-two">
                    <InputFormGroup
                      className="margin-none"
                      name={`additionalContacts[${idx}].email`}
                      placeholder="Email"
                      noLabel
                      showErrorsOnlyOnSubmit
                      submitCount={submitCount}
                    />
                  </Cell>
                )}
                {showPhone && (
                  <Cell className="additionalContactRowInput flex-two">
                    <InputFormGroup
                      className="margin-none"
                      name={`additionalContacts[${idx}].phoneNumber`}
                      placeholder="Phone Number"
                      noLabel
                      type="text"
                      showErrorsOnlyOnSubmit
                      submitCount={submitCount}
                    />
                  </Cell>
                )}
                <Cell className="flex-one">
                  <Button
                    className="expanded margin-none"
                    onClick={(e) => {
                        e.preventDefault();
                        setOpenModal(true);
                        setPendingRemoveIdx(idx);
                    }}
                  >
                    Remove
                  </Button>
                </Cell>
              </Grid>
              <Grid className="additionalContactCheckboxRow">
                <Cell className="flex-two">
                  <CheckBoxFormGroup
                    name={`additionalContacts[${idx}].isReceivingNewJobOpportunities`}
                    title="New Job Opportunities"
                    onCheckedChange={(checked) => SaveEvent(CheckboxEvent(user, 'Additional Contacts', 'New Job Opportunities', checked))}
                  />
                </Cell>
                <Cell className="flex-two">
                  <CheckBoxFormGroup
                    name={`additionalContacts[${idx}].isReceivingJobAwarded`}
                    title="Job Awarded"
                    onCheckedChange={(checked) => SaveEvent(CheckboxEvent(user, 'Additional Contacts', 'Job Awarded', checked))}
                  />
                </Cell>
                <Cell className="flex-two">
                  <CheckBoxFormGroup
                    name={`additionalContacts[${idx}].isReceivingCommunicationResponse`}
                    title="Communication Response"
                    onCheckedChange={(checked) => SaveEvent(CheckboxEvent(user, 'Additional Contacts', 'Communication Response', checked))}
                  />
                </Cell>
              </Grid>
            </React.Fragment>
            ))}
            <Cell className="margin-top">
              <button 
                className="additionalContactsButton"
                type="button"
                onClick={(e) => {
                    if (isDisabled) return;
                    SaveEvent(ClickEvent(user, 'Additional Contacts', 'Add Contact'))
                    push(emptyContact);
                }}
                disabled={isDisabled}
              >
                Add Contact
              </button>
            </Cell>
            <SimpleModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                >
                <SimpleModalContent
                    mainTitle='Bid Response'
                    subTitle='Additional Contacts'
                    identifier={0}
                    message={"Are you sure you want to remove this contact? This action cannot be undone."}
                />
                <Cell className='simple-modal-button-section'>
                   <button 
                      className='button secondary close-simple-modal' 
                      type="button" 
                      onClick={() => setOpenModal(false)}
                    >
                      Close
                    </button>
                   <button 
                     className='button alert action-simple-modal' 
                     type="button"
                     onClick={() => {
                       setOpenModal(false);
                       if (pendingRemoveIdx !== null) {
                           SaveEvent(ClickEvent(user, 'Additional Contacts', 'Remove Contact'))
                           remove(pendingRemoveIdx);
                           setPendingRemoveIdx(null);
                           submitForm();
                       }
                     }}>
                        Remove
                   </button>
               </Cell>
            </SimpleModal>
          </>
        )}
      </FieldArray>
    </Cell>
  );
};

export default AdditionalNotificationContacts;